"use client";

/**
 * Labeled progress header for the long analyze/compare waits.
 *
 * The bare skeletons read as "stuck" to users - nothing moves except
 * a shimmer, there's no wording, no sense of stages. This strip sits
 * ABOVE the skeleton and narrates the pipeline: a moving bar plus a
 * step list (fetch -> extract -> evaluate -> fact-check -> finalize)
 * with the current step spinning and finished steps checked.
 *
 * The server gives no mid-request progress events (one POST), so the
 * bar is TIME-BASED: it advances quickly at first, decelerates, and
 * saturates at 93% until the parent unmounts it (result arrived).
 * Step switching follows elapsed-time thresholds tuned to the real
 * pipeline (fetch ~2s, extraction ~2s, model instant, fact-check LLM
 * the long pole). Honest about being an estimate - the label says
 * how long it usually takes.
 */

import AnimatedLogoMark from "@/components/brand/AnimatedLogoMark";
import { useEffect, useRef, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { ScanAnim } from "@/components/animations/SceneAnimations";
import { CalcAnim } from "@/components/animations/ToolAnimations";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";

interface Step {
  labelKey: TranslationKey;
  /** Elapsed seconds at which this step becomes current. */
  at: number;
}

const ANALYZE_STEPS: Step[] = [
  { labelKey: "progress.step.fetch", at: 0 },
  { labelKey: "progress.step.extract", at: 2.5 },
  { labelKey: "progress.step.evaluate", at: 5 },
  { labelKey: "progress.step.factcheck", at: 8 },
  { labelKey: "progress.step.finalize", at: 14 },
];

// The calculator's valuation (POST /sell): no page to fetch, the model and
// the area's comparables answer in a few seconds, the AI check of a typed
// description takes the rest (owner, 2026-10-19: the same steps, bar and
// mark as the link analysis).
const CALCULATE_STEPS: Step[] = [
  { labelKey: "progress.step.calc_read", at: 0 },
  { labelKey: "progress.step.calc_area", at: 0.8 },
  { labelKey: "progress.step.evaluate", at: 1.6 },
  { labelKey: "progress.step.calc_compare", at: 3 },
  { labelKey: "progress.step.finalize", at: 5 },
];

const COMPARE_STEPS: Step[] = [
  { labelKey: "progress.step.fetch_all", at: 0 },
  { labelKey: "progress.step.evaluate_all", at: 4 },
  { labelKey: "progress.step.compare", at: 9 },
  { labelKey: "progress.step.finalize", at: 15 },
];

/** Time-based pseudo-progress: fast start, asymptotic to ~93%. ``tau`` is
 *  the wait's pace in seconds (the calculator answers faster). */
function pctAt(elapsedS: number, tau = 9): number {
  return Math.min(93, Math.round(100 * (1 - Math.exp(-elapsedS / tau))));
}

export default function AnalysisProgress({
  variant = "analyze",
}: {
  variant?: "analyze" | "compare" | "calculate";
}) {
  const t = useT();
  const calc = variant === "calculate";
  const steps = calc ? CALCULATE_STEPS : variant === "compare" ? COMPARE_STEPS : ANALYZE_STEPS;
  const startRef = useRef<number>(Date.now());
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setElapsed((Date.now() - startRef.current) / 1000),
      250,
    );
    return () => clearInterval(id);
  }, []);

  const pct = pctAt(elapsed, calc ? 3.5 : 9);
  const currentIdx = steps.reduce(
    (acc, s, i) => (elapsed >= s.at ? i : acc),
    0,
  );

  return (
    <div
      className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5"
      role="status"
      aria-live="polite"
    >
      {/* The scan scene - the wait is the app's longest stare, so it
          gets a real illustration: photos slot in, a beam sweeps the
          card, the gauge needle warms up. Seamless loop (a motion dip
          here would read as "hung"). */}
      <div className={calc ? "mx-auto mb-3 h-20 w-20" : "mx-auto mb-3 h-24 w-full max-w-[264px]"}>
        {calc ? <CalcAnim /> : <ScanAnim />}
      </div>

      {/* Bar + percent. The mark builds its house beside the bar for the
          whole wait: the one place every visitor meets the animation
          (owner, 2026-10-18). */}
      <div className="flex items-center gap-3">
        <div className="shrink-0" aria-hidden>
          <AnimatedLogoMark size={32} active loop />
        </div>
        <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gray-900 transition-[width] duration-300 ease-out"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="w-10 shrink-0 text-right text-sm font-bold tabular-nums text-gray-900">
          {pct}%
        </span>
      </div>

      {/* Step list */}
      <ol className="mt-3 space-y-1.5">
        {steps.map((s, i) => {
          const done = i < currentIdx;
          const current = i === currentIdx;
          return (
            <li
              key={s.labelKey}
              className={`flex items-center gap-2 text-[13px] leading-snug ${
                current
                  ? "font-semibold text-gray-900"
                  : done
                    ? "text-gray-500"
                    : "text-gray-400"
              }`}
            >
              {done ? (
                <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : current ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-500" />
              ) : (
                <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-gray-300 dark:border-white/20" />
              )}
              {t(s.labelKey)}
            </li>
          );
        })}
      </ol>

      <p className="mt-3 text-[11px] leading-relaxed text-gray-400">
        {t(calc ? "progress.hint_calc" : "progress.hint")}
      </p>
    </div>
  );
}
